import CardWrapper from "components/Common/CardWrapper";
import { 
  useEffect, 
  useMemo, 
  useState 
} from "react";
import {
  CardHeader, 
  Container, 
  Table
} from "reactstrap";
import { fetchCompetitions, showErrorNotification } from "store/actions";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import { 
  Th, Thead, Tr
} from "react-super-responsive-table";
import { withTranslation } from "react-i18next";
import CustomPagination from "components/Common/CustomPagination";

function Leaderboard(props) {
  const { getCompetitions } = useSelector(state => state.competitionReducer);
  const [selectedComp, setSelectedComp] = useState(props.location.state);
  const [page, setPage] = useState(1);
  const [sizePerPage, setSizePerPage] = useState(10);
    
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(fetchCompetitions());
  }, []);
  
  useEffect(() => {
    // reset pagination when competition changes
    setPage(1);
  }, [selectedComp]);

  const columns = [
    {
      text: props.t("Rank"),
      formatter: (val) => val.rank + 1,
    },
    {
      dateField: "firstName",
      text: props.t("First Name"),
    },
    {
      dateField: "lastName",
      text: props.t("Last Name"),
    },
    {
      dateField: "totalAmount",
      text: props.t("Total"),
      formatter: (val) => val.totalAmount ? parseFloat(val.totalAmount).toFixed(2) : "--",
    },
  ];

  const { layoutMode } = useSelector(state => state.Layout);
  const customStyles = {
    dropdownIndicator: (provided) => ({
      ...provided,
      color: "#495057",
      padding: 0,
      paddingRight: "5px",
    }),
    indicatorSeparator: (provided) => ({
      ...provided,
      display: "none"
    }),
    control: (provided) => {
      if (layoutMode === "dark") {
        return {
          ...provided,
          backgroundColor: "#242632",
          border: 0,
          boxShadow: "0 0.125rem 0.25rem #0B182F",
          color: "#adb5bd",
          height: "100%",
        };
      }
      return {
        ...provided,
      };
    },
    menu: (provided) => ({
      ...provided,
      backgroundColor: layoutMode === "dark" ? "#242632" : "white",
      color: layoutMode === "dark" ? "#adb5bd" : "#495057",
      zIndex: 3,
    }),
    option: (provided, state) => ({
      ...provided,
      display: state.isDisabled ? "none" : "flex",
      flexDirection: "row",
      alignItems: "center",
      color: layoutMode === "dark" ? "#adb5bd" : "#495057",
    }),
    singleValue: (provided) => {
      return {
        ...provided,
        flexDirection: "row",
        alignItems: "center",
        color: layoutMode === "dark" ? "#adb5bd" : "#495057",
      };
    },
  };
  const stats = useMemo(() => selectedComp?.competitionStats || [], [selectedComp]);
  const paginationMeta = useMemo(() => {
    const totalDocs = stats.length;
    const totalPages = Math.ceil(totalDocs / sizePerPage) || 0;
    return {
      totalDocs,
      totalPages,
      hasPrevPage: page > 1,
      hasNextPage: page < totalPages,
      prevPage: page > 1 ? page - 1 : 1,
      nextPage: page < totalPages ? page + 1 : totalPages,
    };
  }, [stats, page, sizePerPage]);

  useEffect(() => {
    // keep page within bounds when page size or data changes
    if (paginationMeta.totalPages > 0 && page > paginationMeta.totalPages) {
      setPage(paginationMeta.totalPages);
    }
  }, [paginationMeta.totalPages, page]);

  const startIndex = (page - 1) * sizePerPage;
  const paginatedStats = useMemo(
    () => stats.slice(startIndex, startIndex + sizePerPage),
    [stats, startIndex, sizePerPage]
  );

  const handlePageChange = (newPage, newSize = sizePerPage) => {
    const limit = Number(newSize) || 10;
    setSizePerPage(limit);
    setPage(newPage || 1);
  };

  const handleSizeChange = (val) => {
    const limit = Number(val) || 10;
    setSizePerPage(limit);
    setPage(1);
  };

  return (
    <>
      <div className="page-content">
        <Container className="mt-5">
          <CardWrapper className="mt-5">
            <CardHeader title="Competitions"></CardHeader>
            <div className="mt-3">
              <div>
                <Select
                  placeholder={props.t("Select competition")}
                  styles= {customStyles}
                  defaultValue={props.location.state?.name ? {
                    label: `${props.location.state?.name} (${props.location.state?.clientType}) (${props.location.state?.type})`,
                    value: props.location.state
                  } : {}}
                  options={getCompetitions.docs?.map((comp) => {
                    return {
                      label: `${comp.name} (${comp.clientType}) (${comp.type})`,
                      value: comp
                    };
                  })}
                  onChange={(val) => {
                    setSelectedComp(val.value);
                  }}
                >
                </Select>
              </div>
              {selectedComp ? (
                <div className="mt-4">
                  <Table borderless responsive className="text-center mb-0">
                    <Thead className="text-center table-light">
                      <Tr>
                        {columns.map((column, index) => (
                          <Th
                            data-priority={index}
                            key={index}
                            className="color-primary"
                          >
                            {column.text}
                          </Th>
                        ))}
                      </Tr>
                    </Thead>
                    <tbody>
                      {paginatedStats?.map((item, rank) => {
                        const displayRank = startIndex + rank;
                        return (
                          <Tr key={displayRank}>
                            {columns.map((column, index) => (
                              <td key={index} data-priority={index}>
                                {column.formatter
                                  ? column.formatter({
                                    ...item,
                                    rank: displayRank,
                                  })
                                  : item[column.dateField]}
                              </td>
                            ))}
                          </Tr>
                        );
                      })}
                    </tbody>
                  </Table>
                  <CustomPagination
                    totalPages={paginationMeta.totalPages}
                    sizePerPage={sizePerPage}
                    page={page}
                    totalDocs={paginationMeta.totalDocs}
                    hasNextPage={paginationMeta.hasNextPage}
                    hasPrevPage={paginationMeta.hasPrevPage}
                    nextPage={paginationMeta.nextPage}
                    prevPage={paginationMeta.prevPage}
                    setSizePerPage={handleSizeChange}
                    onChange={handlePageChange}
                    isClientSide
                  />
                </div>
              ) : (
                <></>
              )}
            </div>
          </CardWrapper>
        </Container>
      </div>
    </>
  );
}

export default withTranslation()(Leaderboard);
